import { IonText, IonToolbar } from '@ionic/react';
import { Form, Formik, FormikProps } from 'formik';
import { cloneDeep, isEmpty, isNaN, isNil } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import { useHasPermission } from '../../../../../../../api/authorization.provider';
import { PlanEntitlementKey } from '../../../../../../../api/graphql/generated';
import { useGetUnitOfMeasureSystem } from '../../../../../../../api/hooks/user-preference.hooks';
import { RightAlignedButtons } from '../../../../../../../components/button/aligned-buttons';
import { CloseButton } from '../../../../../../../components/button/close-button';
import { LoadingButton } from '../../../../../../../components/button/loading-button';
import { useWithConfirmation } from '../../../../../../../components/button/with-confirmation';
import {
  FeatureTour,
  FeatureTourRef,
} from '../../../../../../../components/feature/feature-tour';
import { FeatureTourCard } from '../../../../../../../components/feature/feature-tour-card';
import { AppFeatureKey } from '../../../../../../../components/feature/feature.types';
import {
  IonFormNumericInputLockable,
  NumericInputPreferredUnits,
} from '../../../../../../../components/form/ion-form-numeric-input-lockable';
import { useScratchpadServiceContext } from '../../../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { PaywallBlur } from '../../../../../../../components/paywall/paywall-blur';
import { SaveStatusToast } from '../../../../../../../components/toast/toast.gql';
import { CalloutBanner } from '../../../../../../../components/ui/callout-banner';
import { ContentWithFooter } from '../../../../../../../components/ui/content-with-footer';
import { IntercomArticle } from '../../../../../../../config/intercom';
import { i18nKeyType } from '../../../../../../../i18n';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { logger } from '../../../../../../../util/logger';
import {
  MotionPoint,
  PartUtil,
  ValidateStitchSettingsResult,
} from '../../../../../../../util/part.util';
import {
  FlowUpdateMethod,
  useFlowRefContext,
} from '../../../../../shared/component/flow/flow-ref';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import {
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemSharedRef,
  SystemUnit,
} from '../../../../../shared/model/flow.types';
import {
  FlowItemService,
  FlowService,
} from '../../../../../shared/service/service.types';
import {
  instanceOfFlowItem,
  instanceOfFlowItemSection,
  instanceOfHelper,
} from '../../../../../shared/util/flow-item.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { instanceOfFlowItemStitchSectionParent } from '../../../util/beacon-welding.util';
import { StitchModifiedInfo } from './flow-item-stitch-modified-info';
import { useFlowItemEnhancedStitchTourSteps } from './flow-item-stitch-tour';
import {
  SystemUnitOfMeasure,
  convertStitchInputValueToUom,
  convertStitchSystemValueToUom,
  getReadableValue,
} from './flow-item-stitch.utils';

const schema = yup.object().shape({
  stitchLength: yup
    .number()
    .required()
    .moreThan(0)
    .when(
      ['pitch', 'stitchLengthLocked', 'pitchLocked', 'totalSequenceLength'],
      (
        [pitch, stitchLengthLocked, pitchLocked, totalSequenceLength],
        schema,
        { value }
      ) => {
        const bothLocked = pitchLocked && stitchLengthLocked;

        if (!isNil(pitch) && pitch > 0 && pitch < value) {
          return schema.lessThan(
            yup.ref('pitch'),
            getLocalizedText('welding:stitch_length_less_than_equal_pitch')
          );
        }

        if (bothLocked) {
          return schema.lessThan(
            yup.ref('totalSequenceLength'),
            getLocalizedText('welding:stitch_length_less_than_total_length')
          );
        }

        // modificated settings attempt to fit two stitches
        return schema.lessThan(totalSequenceLength / 2);
      }
    ),
  stitchLengthLocked: yup.boolean().required(),
  pitch: yup
    .number()
    .required()
    .moreThan(0)
    .lessThan(
      yup.ref('totalSequenceLength'),
      getLocalizedText('welding:stitch_pitch_less_than_total_length')
    ),
  pitchLocked: yup.boolean().required(),
  totalSequenceLength: yup.number().required(),
});

type FormData = BeaconWeldingData.StitchSettingsFormData & {
  // this is a convenience for yup validation
  totalSequenceLength: number;
};

enum StitchStatus {
  EDITING = 'EDITING',
  ERROR = 'ERROR',
  CALCULATING = 'CALCULATING',
  CALCULATED = 'CALCULATED',
  SAVING = 'SAVING',
}

const InputDisabledStatuses = [StitchStatus.CALCULATING, StitchStatus.SAVING];

interface FlowItemStitchProps {
  startItem: FlowItem;
  onStitchConverted: () => void;
}

const FlowItemStitch = (props: FlowItemStitchProps): ReactElement => {
  const { startItem, onStitchConverted } = props;

  const { flow, flowAnalyticService, flowItemService, flowService } =
    useFlowServices<
      BeaconWeldingData.FlowConfig,
      typeof BeaconWeldingData.FlowSectionType
    >();
  const { saveFlow } = useFlowRefContext();
  const { scratchpad } = useScratchpadServiceContext();
  const system = useGetUnitOfMeasureSystem();
  const { i18n } = useTranslation();

  const [status, setStatus] = useState<StitchStatus>(StitchStatus.EDITING);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const isEditing = instanceOfFlowItemStitchSectionParent(startItem);

  const formikRef = useRef<FormikProps<FormData> | null>(null);

  const defaultStitchValues: BeaconWeldingData.StitchSettingsFormData = {
    stitchLengthLocked: true,
    pitchLocked: false,
  };

  const initialStitchSettingsValues: BeaconWeldingData.StitchSettingsFormData =
    isEditing
      ? {
          ...startItem.attributes.stitchSettings,
          pitch: convertStitchSystemValueToUom(
            startItem.attributes.stitchSettings.pitch,
            system,
            3
          ),
          stitchLength: convertStitchSystemValueToUom(
            startItem.attributes.stitchSettings.stitchLength,
            system,
            3
          ),
        }
      : defaultStitchValues;

  const [stitchSettings, setStitchSettings] =
    useState<BeaconWeldingData.StitchSettingsFormData>(
      initialStitchSettingsValues
    );
  const [validatedSettingsResult, setValidatedSettingsResult] = useState<
    ValidateStitchSettingsResult | undefined
  >(undefined);

  const startIndex = flow.items.findIndex((i) => i.id === startItem.id);

  if (!isEditing && isNil(startIndex)) {
    logger.error(
      'Cannot process stitch. No start index was found for the segment.',
      {
        scratchpadId: scratchpad.id,
        scratchpadVersion: scratchpad.version,
        startItem: startItem,
      }
    );

    throw new Error('Cannot process stitch. Failed to build flow segment.');
  }

  const endIndex = useMemo(() => {
    if (isEditing) {
      return flow.items.findIndex(
        (i) => !instanceOfHelper(i) && i.id === startItem.attributes.endItemId
      );
    } else {
      return flowItemService.getEndOfSequenceIndexFromFlowItem(
        startItem,
        flow.items
      );
    }
  }, [flow.items, flowItemService, isEditing, startItem]);

  if (isNil(endIndex)) {
    logger.error(
      'Cannot process stitch. No end index was found for the segment.',
      {
        scratchpadId: scratchpad.id,
        scratchpadVersion: scratchpad.version,
        startItem: startItem,
      }
    );

    throw new Error('Cannot process stitch. Failed to build flow segment.');
  }

  const isInSection =
    !isEditing &&
    startIndex > -1 &&
    flow.items[startIndex - 1]?.ref === FlowItemSharedRef.SECTION;

  const originalItems: FlowItemArrayType[] = useMemo(() => {
    if (isEditing) {
      return startItem.attributes.originalItems;
    } else {
      // we will include the encapsulating section if it exists
      const previousItem = startIndex ? flow.items[startIndex - 1] : undefined;
      const segment = cloneDeep(flow.items.slice(startIndex, endIndex + 1));
      return !isNil(previousItem) && instanceOfFlowItemSection(previousItem)
        ? [cloneDeep(previousItem), ...segment]
        : segment;
    }
  }, [
    endIndex,
    flow.items,
    isEditing,
    startIndex,
    startItem?.attributes?.originalItems,
  ]);

  const originalPathItems = useMemo(() => {
    return originalItems
      .filter(instanceOfFlowItem)
      .filter((i) => !instanceOfFlowItemSection(i));
  }, [originalItems]);

  const stitchPoints: MotionPoint[] = useMemo(() => {
    return flowItemService.convertFlowItemsToMotionPoints(originalPathItems);
  }, [flowItemService, originalPathItems]);

  const totalSequenceLength = getConvertedTotalSequenceLength({
    stitchPoints,
    system,
  });

  const initialFormValues: FormData = {
    ...initialStitchSettingsValues,
    totalSequenceLength,
  };

  const currentStitchCount = flow.items.filter(
    instanceOfFlowItemStitchSectionParent
  ).length;

  const creationIndex = isEditing
    ? startItem.attributes.creationIndex
    : currentStitchCount + 1;

  const setErrorStatus = useCallback((message?: i18nKeyType) => {
    const errorMessage = getLocalizedText(
      message ?? 'common:stitch_generation_error'
    );
    setErrorMessage(errorMessage);
    setStatus(StitchStatus.ERROR);
  }, []);

  const validateStitch = useCallback(
    async (values: BeaconWeldingData.StitchSettingsFormData) => {
      const { stitchLengthLocked, pitchLocked } = values;
      const stitchLength = Number(values.stitchLength);
      const pitch = Number(values.pitch);

      if (
        isNaN(stitchLength) ||
        isNaN(pitch) ||
        stitchLength <= 0 ||
        pitch <= 0
      ) {
        return;
      }

      setStatus(StitchStatus.CALCULATING);
      setStitchSettings(values);

      const lengthOfStitch = convertStitchInputValueToUom(stitchLength, system);
      const pitchOfStitch = convertStitchInputValueToUom(pitch, system);

      let validatedResult: ValidateStitchSettingsResult | undefined = undefined;

      try {
        validatedResult = PartUtil.validateStitchSettings(
          stitchPoints,
          {
            lengthOfStitch,
            pitchOfStitch,
          },
          {
            lockPitch: pitchLocked,
            lockStitchLength: stitchLengthLocked,
          }
        );
      } catch (error) {
        logger.error('Failed to validate stitch segments.', {
          error,
          scratchpadId: scratchpad.id,
          startItemId: startItem.id,
          stitchSettings: {
            lengthOfStitch,
            pitchOfStitch,
            pitchLocked,
            stitchLengthLocked,
          },
        });

        setErrorStatus();
        return;
      }

      setValidatedSettingsResult(validatedResult);
      setStatus(StitchStatus.CALCULATED);
    },
    [scratchpad.id, setErrorStatus, startItem.id, stitchPoints, system]
  );

  const convertStitch = useCallback(async () => {
    if (isNil(validatedSettingsResult)) {
      logger.error(
        'Failed to generate stitch. Modified settings are undefined.',
        {
          scratchpadId: scratchpad.id,
          startItemId: startItem.id,
        }
      );

      setErrorStatus();
      return;
    }

    setStatus(StitchStatus.SAVING);

    const { lengthOfStitch, pitchOfStitch } =
      validatedSettingsResult.modifiedSettings;

    const result = await convertPointsToStitch({
      stitchSettings: {
        stitchLength: lengthOfStitch,
        pitch: pitchOfStitch,
        pitchLocked: stitchSettings.pitchLocked,
        stitchLengthLocked: stitchSettings.stitchLengthLocked,
      },
      stitchPoints,
      stitchNumber: creationIndex,
      originalItems,
      isInSection,
      startIndex,
      endIndex,
      defaultItem: originalPathItems[0],
      flow,
      flowItemService,
      flowService,
    });

    if (result.error) {
      logger.error('Failed to convert stitch.', {
        scratchpadId: scratchpad.id,
        startItemId: startItem.id,
        stitchSettings: {
          lengthOfStitch,
          pitchOfStitch,
        },
        message: result.error.message,
        ...result.error.context,
      });

      setErrorStatus();
      return;
    }

    const updatedFlow = result.convertedSegments;

    flowAnalyticService.trackStitchConversion({
      isEditing,
      scratchpadId: scratchpad.id,
      originItemId: originalPathItems[0].id ?? 'unknown',
      sectionItemId: updatedFlow[0].id ?? 'unknown',
      settings: {
        pitch: pitchOfStitch,
        stitchLength: lengthOfStitch,
        pitchLocked: stitchSettings?.pitchLocked ?? false,
        stitchLengthLocked: stitchSettings?.stitchLengthLocked ?? false,
      },
    });

    saveFlow(updatedFlow, {
      updateMethod: FlowUpdateMethod.SERVER_WITH_OPTIMISTIC_UPDATE,
      saveAsDelta: true,
    });

    setStatus(StitchStatus.EDITING);

    onStitchConverted();
  }, [
    validatedSettingsResult,
    stitchSettings.pitchLocked,
    stitchSettings.stitchLengthLocked,
    stitchPoints,
    creationIndex,
    originalItems,
    isInSection,
    startIndex,
    endIndex,
    originalPathItems,
    flow,
    flowItemService,
    flowService,
    flowAnalyticService,
    isEditing,
    scratchpad.id,
    saveFlow,
    onStitchConverted,
    startItem.id,
    setErrorStatus,
  ]);

  const withConfirmation = useWithConfirmation({
    header: getLocalizedText('common:stitch_replace_confirmation'),
  });

  const handleConvert = useCallback(() => {
    if (isEditing) {
      withConfirmation({
        onConfirmation: convertStitch,
      });
    } else {
      convertStitch();
    }
  }, [convertStitch, isEditing, withConfirmation]);

  const preferredUnits: NumericInputPreferredUnits = {
    imperial: SystemUnitOfMeasure.INCH,
    metric: SystemUnitOfMeasure.CENTIMETER,
  };

  const canUpdateScratchpad = useHasPermission(
    'update',
    'scratchpad',
    scratchpad
  );

  const [disablePaywallBlur, setDisablePaywallBlur] = useState<boolean>(false);
  const tourRef = useRef<FeatureTourRef>(null);
  const featureTourSteps = useFlowItemEnhancedStitchTourSteps(tourRef);

  const inputsDisabled =
    !disablePaywallBlur &&
    (InputDisabledStatuses.includes(status) || !canUpdateScratchpad);

  return (
    <>
      <FeatureTour
        ref={tourRef}
        appFeatureKey={AppFeatureKey.WELDING_ENHANCED_STITCH}
        planEntitlementKey={PlanEntitlementKey.WELDING_ENHANCED_STITCH}
        steps={featureTourSteps}
        onClose={() => {
          setDisablePaywallBlur(false);
        }}
        popoverProps={{
          backdropDismiss: false,
        }}
      />
      <Formik
        validationSchema={schema}
        enableReinitialize={true}
        initialValues={initialFormValues}
        onSubmit={handleConvert}
        innerRef={(instance) => (formikRef.current = instance)}
      >
        {({ errors, values, setFieldValue, dirty, isValid }) => {
          const submitDisabled =
            !isEmpty(errors) ||
            status !== StitchStatus.CALCULATED ||
            isNil(validatedSettingsResult) ||
            !dirty;

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const hasChangedOnce = useRef<boolean>(false);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            if (!dirty || !isValid) {
              // this is to prevent clearing the result if a change didn't occur
              if (hasChangedOnce.current) {
                setValidatedSettingsResult(undefined);
              }
              return;
            }

            hasChangedOnce.current = true;

            validateStitch(values);
          }, [dirty, errors, isValid, values]);

          const footer = (
            <IonToolbar>
              <RightAlignedButtons>
                <CloseButton
                  label={
                    (false as any)
                      ? i18n.t('common:done')
                      : i18n.t('common:cancel')
                  }
                  onClose={() => {
                    if (typeof onStitchConverted === 'function') {
                      onStitchConverted();
                    }
                  }}
                />
                <LoadingButton
                  loading={status === StitchStatus.SAVING}
                  buttonProps={{
                    disabled: submitDisabled || !canUpdateScratchpad,
                    onClick: () => {
                      formikRef.current?.submitForm();
                    },
                  }}
                >
                  {isEditing
                    ? getLocalizedText('common:stitch_replace')
                    : getLocalizedText('common:convert_to_stitch')}
                </LoadingButton>
              </RightAlignedButtons>
            </IonToolbar>
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const handleLockToggle = useCallback(
            (name: keyof BeaconWeldingData.StitchSettingsFormData) => {
              const { stitchLengthLocked, pitchLocked } = values;

              if ((stitchLengthLocked && pitchLocked) || !values[name]) {
                setFieldValue(name, !values[name]);
              } else {
                setErrorStatus('common:stitch_settings_lock_required_error');
                setStatus(StitchStatus.ERROR);
              }
            },
            [setFieldValue, values]
          );

          return (
            <ContentWithFooter
              footer={footer}
              contentProps={{ scrollY: false }}
            >
              <>
                <PaywallBlur
                  planEntitlementKey={
                    PlanEntitlementKey.WELDING_ENHANCED_STITCH
                  }
                  blurId="enhanced-stitch-paywall"
                  disableBlur={disablePaywallBlur}
                  disableClick={true}
                  customAction={() => {
                    return (
                      <FeatureTourCard
                        title={
                          getLocalizedText('common:enhanced_stitch') ??
                          'Enhanced Stitch'
                        }
                        content={
                          getLocalizedText(
                            'plan:enhanced_stitch_weld_feature_description'
                          ) ??
                          'Create stitch segments from complex weld paths that can be reordered and customized.'
                        }
                        intercomArticle={IntercomArticle.ENHANCED_STITCH_WELD}
                        onStartTour={() => {
                          setDisablePaywallBlur(true);
                          tourRef.current?.startTour();
                        }}
                      />
                    );
                  }}
                >
                  <CalloutBanner
                    message={
                      getLocalizedText(
                        'common:stitch_settings_item_default_info'
                      ) ??
                      'All generated items will utilize the same settings as the first item. Please evaluate the generated items and adjust the settings as needed.'
                    }
                    calloutBannerProps={{ className: 'ion-padding' }}
                  />

                  <Form>
                    <div className="stitch-container">
                      <IonFormNumericInputLockable
                        name="stitchLength"
                        label={
                          getLocalizedText('common:stitch_length') ??
                          'Stitch Length'
                        }
                        preferredUnits={preferredUnits}
                        isLocked={values.stitchLengthLocked}
                        onToggleLock={() => {
                          handleLockToggle('stitchLengthLocked');
                        }}
                        itemProps={{
                          lines: 'full',
                          disabled: inputsDisabled,
                        }}
                        inputProps={{
                          step: '0.01',
                          inputMode: 'decimal',
                          placeholder: '0',
                          disabled: inputsDisabled,
                        }}
                        lockProps={{
                          id: 'enhanced-stitch-length-lock',
                        }}
                      />
                      <IonFormNumericInputLockable
                        name="pitch"
                        label={
                          getLocalizedText('common:stitch_pitch') ?? 'Pitch'
                        }
                        value={values.pitch}
                        preferredUnits={preferredUnits}
                        isLocked={values.pitchLocked}
                        onToggleLock={() => {
                          handleLockToggle('pitchLocked');
                        }}
                        itemProps={{
                          lines: 'full',
                          disabled: inputsDisabled,
                          id: 'enhanced-stitch-inputs',
                        }}
                        inputProps={{
                          step: '0.01',
                          inputMode: 'decimal',
                          placeholder: '0',
                          disabled: inputsDisabled,
                        }}
                      />
                      <div className="ion-padding">
                        <IonText color={'medium'}>
                          {getLocalizedText(
                            'welding:stitch_total_path_length',
                            {
                              totalPathLength: getReadableValue(
                                totalSequenceLength,
                                system
                              ),
                            }
                          )}
                        </IonText>
                      </div>
                      {!isNil(stitchSettings) &&
                        !isNil(validatedSettingsResult) && (
                          <StitchModifiedInfo
                            stitchSettings={stitchSettings}
                            validatedSettingsResult={validatedSettingsResult}
                            system={system}
                            preferredUnits={preferredUnits}
                          />
                        )}
                    </div>
                  </Form>
                </PaywallBlur>
              </>
            </ContentWithFooter>
          );
        }}
      </Formik>
      <SaveStatusToast
        showOnly="error"
        error={errorMessage}
        onDidDismiss={() => {
          setStatus(StitchStatus.EDITING);
          setErrorMessage(undefined);
        }}
      />
    </>
  );
};

const getConvertedTotalSequenceLength = (params: {
  stitchPoints: MotionPoint[];
  system: SystemUnit;
}) => {
  const { stitchPoints, system } = params;

  const totalSequenceLength =
    PartUtil.calculateTotalSegmentLength(stitchPoints);

  const convertedTotalSequenceLength = convertStitchSystemValueToUom(
    totalSequenceLength,
    system,
    3
  );

  return convertedTotalSequenceLength;
};

interface ConvertStitchParams {
  stitchSettings: Required<BeaconWeldingData.StitchSettingsFormData>;
  stitchPoints: MotionPoint[];
  stitchNumber: number;
  originalItems: FlowItemArrayType[];
  isInSection: boolean;
  startIndex: number;
  endIndex: number;
  defaultItem: FlowItem;
  flow: Flow;
  flowItemService: FlowItemService<
    BeaconWeldingData.FlowConfig,
    typeof BeaconWeldingData.FlowSectionType
  >;
  flowService: FlowService;
}

interface ConvertStitchResultSuccess {
  error: undefined;
  convertedSegments: FlowItem[];
}

interface ConvertStitchResultError {
  error: {
    message: string;
    context?: Record<string, unknown>;
  };
  convertedSegments: undefined;
}

type ConvertStitchResult =
  | ConvertStitchResultSuccess
  | ConvertStitchResultError;

const convertPointsToStitch = async (
  params: ConvertStitchParams
): Promise<ConvertStitchResult> => {
  const {
    stitchSettings,
    stitchPoints,
    stitchNumber,
    isInSection,
    startIndex,
    endIndex,
    originalItems,
    defaultItem,
    flow,
    flowItemService,
    flowService,
  } = params;
  const { pitch, stitchLength, pitchLocked, stitchLengthLocked } =
    stitchSettings;

  let generatedSegments: MotionPoint[] = [];

  try {
    generatedSegments = PartUtil.generateStitchSegments(stitchPoints, {
      lengthOfStitch: stitchLength,
      pitchOfStitch: pitch,
    });
  } catch (error) {
    return {
      error: {
        message: 'Failed to generate stitch segments.',
        context: { error },
      },
      convertedSegments: undefined,
    };
  }

  if (generatedSegments.length === 0) {
    return {
      error: { message: 'No segments were generated.' },
      convertedSegments: undefined,
    };
  }

  let convertedSegments: FlowItem[] = [];

  try {
    convertedSegments = flowItemService.convertMotionPointsToFlowItemSections({
      motionPoints: generatedSegments,
      defaultItem,
      sectionDetails: {
        nameKey: 'common:segment',
        numericPrefix: stitchNumber,
        labelSuffixType: 'char',
        sectionType: BeaconWeldingData.FlowSectionType.STITCH,
      },
    });
  } catch (error) {
    return {
      error: {
        message: 'Failed to convert stitch segment to flow items.',
        context: { error },
      },
      convertedSegments: undefined,
    };
  }

  const flowValidationErrors = await flowService.validate({
    ...flow,
    items: convertedSegments,
  });

  if (!isEmpty(flowValidationErrors)) {
    return {
      error: {
        message: 'Generated stitch sequence is invalid.',
        context: { flowValidationErrors },
      },
      convertedSegments: undefined,
    };
  }

  const defaultParentSectionItem =
    flowItemService.getDefaultItemForRef<BeaconWeldingData.FlowItemStitchSectionParentAttributes>(
      FlowItemSharedRef.SECTION
    );

  const storedSettings: Required<BeaconWeldingData.StitchSettingsFormData> = {
    pitch: pitch,
    stitchLength: stitchLength,
    pitchLocked: pitchLocked,
    stitchLengthLocked: stitchLengthLocked,
  };

  const parentSectionItem: BeaconWeldingData.FlowItemStitchSectionParent = {
    ...defaultParentSectionItem,
    id: uuid(),
    name: `${getLocalizedText('common:stitch')} ${stitchNumber}`,
    attributes: {
      isParent: true,
      type: BeaconWeldingData.FlowSectionType.STITCH,
      endItemId: convertedSegments[convertedSegments.length - 1].id,
      originalItems,
      creationIndex: stitchNumber,
      stitchSettings: storedSettings,
    },
  };

  const middle = [parentSectionItem, ...convertedSegments];

  const modifiedStartIndex = isInSection ? startIndex - 1 : startIndex;
  const start = flow.items.slice(0, modifiedStartIndex);
  const end = flow.items.slice(endIndex + 1);

  const updatedFlow: FlowItem[] = [...start, ...middle, ...end];

  return {
    error: undefined,
    convertedSegments: updatedFlow,
  };
};

export { FlowItemStitch };
